<template>
  <LayoutMinimal>
    <ModalCreateTenant
      @created-tenant="onCreatedTenant"
    />
    <div :style="style.container">
      <HeaderLargePrimary
        :truncate="true"
        :style="style.header"
      >
        Select your workspace
      </HeaderLargePrimary>
      <TabPrimary
        v-model="tabIndex"
        :tabs="getTabs"
        :style="style.tab"
      />
      <template v-if="tabIndex === 0">
        <FlexCardListPrimary
          :divisor="12"
          :elementLength="userTenants.length + 1"
          :style="style.cards"
        >
          <template v-slot:element="props">
            <CardCreateTenant
              v-if="props.index === 0"
              v-on="$listeners"
              @create="openModalToCreateTenant"
            />
            <CardAccessTenant
              v-if="props.index > 0"
              v-on="$listeners"
              :tenant="userTenants[props.index - 1]"
              @select="selectTenant"
            />
          </template>
        </FlexCardListPrimary>
        <PaginationPrimary
          v-if="userTenantsPageCount > 1"
          v-model="userTenantsPage"
          :pageCount="userTenantsPageCount"
          @change="fetchCurrentUserTenants"
          :style="style.pagination"
        />
      </template>
      <template v-else-if="tabIndex === 1">
        <FlexCardListPrimary
          :divisor="12"
          :elementLength="tenantInvitationCodes.length"
          :style="style.cards"
        >
          <template v-slot:element="props">
            <CardAccessTenant
              v-on="$listeners"
              action="join"
              :invitationCode="tenantInvitationCodes[props.index].invitationCode"
              :tenant="tenantInvitationCodes[props.index].tenant"
              @accept-invitation="acceptInvitation"
              @decline-invitation="declineInvitation"
            />
          </template>
        </FlexCardListPrimary>
        <PaginationPrimary
          v-if="tenantInvitationCodesPageCount > 1"
          v-model="tenantInvitationCodesPage"
          :pageCount="tenantInvitationCodesPageCount"
          @change="fetchCurrentUserTenantInvitationCodes"
          :style="style.pagination"
        />
      </template>
    </div>
  </LayoutMinimal>
</template>

<script>
  import ApiCurrentUserTenant from '@js/api/current-user-tenant'
  import ApiCurrentUserTenantInvitationCode
    from '@js/api/current-user-tenant-invitation-code'
  import ApiCurrentUserTenantInvitationCodeAccept
    from '@js/api/current-user-tenant-invitation-code-accept'
  import ApiCurrentUserTenantInvitationCodeDecline
    from '@js/api/current-user-tenant-invitation-code-decline'
  import CardAccessTenant from '@tenants/modules/card-access-tenant'
  import CardCreateTenant from '@tenants/modules/card-create-tenant'
  import FlexCardListPrimary from '@modules/v2/flex-list/flex-card-list-primary'
  import HeaderLargePrimary from '@modules/v2/header/header-large-primary'
  import LayoutMinimal from '@modules/v2/layout/layout-minimal'
  import ModalCreateTenant from '@components/tenants/modals/modal-create-tenant'
  import PaginationPrimary from '@modules/v2/pagination/pagination-primary'
  import TabPrimary from '@modules/v2/tab/tab-primary'
  import { Layout } from '@js/styles'
  import { MODAL_WINDOW_NAMES, PAGE_SIZES } from '@js/constants'

  export default {
    name: 'UserTenants',
    components: {
      CardAccessTenant,
      CardCreateTenant,
      FlexCardListPrimary,
      HeaderLargePrimary,
      LayoutMinimal,
      ModalCreateTenant,
      PaginationPrimary,
      TabPrimary,
    },
    data: function () {
      return {
        pageSize: PAGE_SIZES.USER_TENANT,
        tabIndex: 0,
        tenantInvitationCodes: [],
        tenantInvitationCodesCount: 0,
        tenantInvitationCodesPage: 1,
        userTenants: [],
        userTenantsCount: 0,
        userTenantsPage: 1,
        style: {
          cards: {
            width: '100%',
          },
          container: {
            alignItems: 'center',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: Layout.container.primary.maxWidth,
            width: '100%',
          },
          header: {
            margin: `${Layout.space.lg} 0`,
            padding: `0 ${Layout.space.lg}`,
            width: '100%',
          },
          pagination: {
            display: 'flex',
            justifyContent: 'center',
            margin: `${Layout.space.lg} 0`,
            width: '100%',
          },
          tab: {
            padding: `0 ${Layout.space.lg}`,
            width: '100%',
          },
        },
      };
    },
    created: async function () {
      await this.fetchCurrentUserTenants();
      await this.fetchCurrentUserTenantInvitationCodes();
    },
    computed: {
      getTabs: function () {
        return [
          {
            label: `Your Workspace ${this.userTenantsCount}`,
            disabled: false,
          },
          {
            label: `Invitation ${this.tenantInvitationCodesCount}`,
            disabled: this.tenantInvitationCodesCount === 0,
          },
        ];
      },
      tenantInvitationCodesPageCount: function () {
        return Math.floor((this.tenantInvitationCodesCount - 1) / this.pageSize) + 1;
      },
      userTenantsPageCount: function () {
        return Math.floor((this.userTenantsCount - 1) / this.pageSize) + 1;
      },
    },
    methods: {
      acceptInvitation: async function (invitationCode) {
        try {
          const payload = {
            invitationCode: invitationCode,
          };
          const api = await new ApiCurrentUserTenantInvitationCodeAccept();
          const res = await api.post(payload);
          this.tenantInvitationCodesPage = await 1;
          await this.$store.commit(
            'notification/setMessage',
            `Joined ${res.data.name} workspace.`,
          );
          await this.fetchCurrentUserTenants();
          await this.fetchCurrentUserTenantInvitationCodes();
        } catch (error) {
          this.handleError(error);
        }
      },
      declineInvitation: async function (invitationCode) {
        try {
          const payload = {
            invitationCode: invitationCode,
          };
          const api = await new ApiCurrentUserTenantInvitationCodeDecline();
          const res = await api.post(payload);
          this.tenantInvitationCodesPage = await 1;
          await this.$store.commit(
            'notification/setWarning',
            `Declined invitation of ${res.data.name}.`,
          );
          await this.fetchCurrentUserTenants();
          await this.fetchCurrentUserTenantInvitationCodes();
        } catch (error) {
          this.handleError(error);
        }
      },
      fetchCurrentUserTenants: async function () {
        try {
          const queryParams = {
            page: this.userTenantsPage,
            pageSize: this.pageSize,
          };
          const api = await new ApiCurrentUserTenant();
          const res = await api.get(queryParams);
          this.userTenants = await res.data.results;
          this.userTenantsCount = await res.data.count;
        } catch (error) {
          this.handleError(error, 'Failed to fetch workspaces.');
        }
      },
      fetchCurrentUserTenantInvitationCodes: async function () {
        try {
          const queryParams = {
            page: this.tenantInvitationCodesPage,
            pageSize: this.pageSize,
          };
          const api = await new ApiCurrentUserTenantInvitationCode();
          const res = await api.get(queryParams);
          this.tenantInvitationCodes = await res.data.results;
          this.tenantInvitationCodesCount = await res.data.count;
        } catch (error) {
          this.handleError(error, 'Failed to fetch invitations');
        }
      },
      handleError: function (error, message = '') {
        console.error(error);
        this.$store.commit('notification/setWarning', message);
      },
      onCreatedTenant: async function () {
        await this.fetchCurrentUserTenants();
      },
      openModalToCreateTenant: function () {
        this.$modal.show(MODAL_WINDOW_NAMES.CREATE_TENANT_PRIMARY);
      },
      selectTenant: async function (tenant) {
        await this.$store.commit('tenant/setTenantDomain', tenant.domain);
        await this.$router.push({ name: 'private-home' });
      },
    },
    watch: {
      tenantInvitationCodesCount: async function (newVal, oldVal) {
        if (newVal === 0) {
          this.tabIndex = 0;
          this.userTenantsPage = 1;
          await this.fetchCurrentUserTenants();
        }
      }
    },
  };
</script>
